import { Inject } from '@odinjs/odin';

import { Address, catchRenderException, City, Endpoint, FormViewService, ProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { FetchAPI, FieldAPI, FormAPI, InputAPI, RadioGroupAPI, RadioGroupItem } from '@philips-emr/tws-components';
import Controller, { OnBlur, OnChange, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import componentApi from '@philips-emr/tws-core/lib/component-api';
import { ChangeEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';

import { ProviderAccreditationEstablishmentAliases as Aliases } from '../../codegen';
import BaseController from '../BaseController';

enum IeTypeSecretary {
  STATE = 'E',
  MUNICIPAL = 'M',
}

enum IeEstablishment {
  HEAD = 'M',
  BRANCH = 'F',
}

@Controller({ fragment: 'provider-accreditation-establishment' })
export default class ProviderAccreditationEstablishmentController extends BaseController {

  @Inject('form-establishment')
  private formEstablishment: FormAPI;

  @Inject('fetch-city')
  private fetchCity: FetchAPI;

  @Inject('field-city')
  private fieldCity: FieldAPI;

  @Inject('radio-group-ieTypeSecretary')
  private radioGroupIeTypeSecretary: RadioGroupAPI;

  @Inject('radio-group-establishment-type')
  private radioGroupEstablishmentType: RadioGroupAPI;

  @Inject(Aliases.FIELD_CNPJ_MATRIZ)
  private headquartersIdField: InputAPI;

  @Inject(Aliases.INPUT_CPNPJ_MATRIZ)
  private headquartersIdInput: InputAPI;

  private city: City | undefined;

  private readonly ieTypeSecretaryMunicipal: RadioGroupItem = {
    key: IeTypeSecretary.MUNICIPAL,
    label: 'pa.municipal',
    value: IeTypeSecretary.MUNICIPAL,
  };

  private readonly ieTypeSecretaryState: RadioGroupItem = {
    key: IeTypeSecretary.STATE,
    label: 'pa.state-2',
    value: IeTypeSecretary.STATE,
  };

  private readonly ieTypeHead: RadioGroupItem = {
    key: IeEstablishment.HEAD,
    label: 'pa.headquarters',
    value: IeEstablishment.HEAD,
  };

  private readonly ieTypeBranch: RadioGroupItem = {
    key: IeEstablishment.BRANCH,
    label: 'pa.branch',
    value: IeEstablishment.BRANCH,
  };

  @OnBlur('field-cdCnes')
  public onBlurCNES(event: FocusEvent): void {

    const api = event.target as unknown as FieldAPI;
    const cnes = api.getValue();
    // pay attention: when CNES's webservice has been implement you should have to implements the hard code to get the values from it here
    if (cnes) {
      console.log('disable all matched fields with value');
    } else {
      console.log('enable all matched fields without value');
    }
  }

  @OnMount('panel-provider-accreditation-establishment')
  public onMountPanelProviderAccreditationEstablishment(): void {
    this.city = undefined;

    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqUser, nrSeqAccreditation });

    if (!nrSeqAccreditation) {
      this.setRadioButtonOptions();
      this.setRadioButtonEstablishmentOptions();
      return;
    }

    void HttpService.get<ProviderAccreditation>(Endpoint.GET.PROVIDER_ESTABLISHMENT_ITEM, { nrSeqUser, nrSeqAccreditation })
      .then(resp => {
        const body = resp.body;
        const { sgState, ieTypeSecretary, ieEstablishment, ieStatus, ieSus } = body as unknown as ProviderAccreditation;
        catchRenderException(() => {
          this.setParameterFetchCity(sgState);
          this.setRadioButtonOptions();
          this.setRadioButtonEstablishmentOptions();
          let itemSelected!: RadioGroupItem;
          this.formEstablishment.setData({ ...body, ieEstablishment: this.setEstablishmentSelected(ieEstablishment, itemSelected), ieTypeSecretary: this.setTypeSecretarySelected(ieTypeSecretary, itemSelected), ieSus: ieSus == 'true' });
          this.formEstablishment.validate();
          if (ieStatus != '1') {
            this.formEstablishment.setState({ editing: false });
            this.formEstablishment.setState({ disabled: true });
          }

          if (ieEstablishment == IeEstablishment.BRANCH) {
            this.headquartersIdField.setValidators([{ name: 'required', options: {} }] as Validator[]);
            this.headquartersIdField.validate();
          } else {
            this.headquartersIdInput.setEnabled(false);
          }
        });
      });
  }

  private setTypeSecretarySelected(ieTypeSecretary: string, itemSelected: RadioGroupItem): RadioGroupItem {
    if (ieTypeSecretary === IeTypeSecretary.MUNICIPAL) {
      itemSelected = this.ieTypeSecretaryMunicipal;
    } else if (ieTypeSecretary === IeTypeSecretary.STATE) {
      itemSelected = this.ieTypeSecretaryState;
    }

    if (itemSelected) {
      this.radioGroupIeTypeSecretary.setValue(itemSelected);
    }
    return itemSelected;
  }

  private setEstablishmentSelected(ieEstablishment: string, itemSelected: RadioGroupItem): RadioGroupItem {
    if (ieEstablishment === IeEstablishment.HEAD) {
      itemSelected = this.ieTypeHead;
    } else if (ieEstablishment === IeEstablishment.BRANCH) {
      itemSelected = this.ieTypeBranch;
    }

    if (itemSelected) {
      this.radioGroupIeTypeSecretary.setValue(itemSelected);
    }
    return itemSelected;
  }

  private setRadioButtonOptions(): void {
    catchRenderException(() => {
      this.radioGroupIeTypeSecretary.setData([this.ieTypeSecretaryMunicipal, this.ieTypeSecretaryState]);
    });
  }

  @OnSubmit('form-establishment')
  public onSubmitForm(event: FormAPI): void {
    const { record: body } = event;
    const { nrSeqUser, nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const ieTypeSecretary = body.ieTypeSecretary as RadioGroupItem;
    const ieEstablishment = body.ieEstablishment as RadioGroupItem;

    this.save(!!nrSeqAccreditation, { nrSeqUser, nrSeqAccreditation }, { ...body, nrSeqTypeAccreditation, ...this.city, ieEstablishment: ieEstablishment ?.key, ieTypeSecretary: ieTypeSecretary ?.key });
  }

  private save(isEditing: boolean, params: {}, body: {}): void {
    if (isEditing) {
      void HttpService.put(Endpoint.PUT.PROVIDER_ESTABLISHMENT, params, { body });
      return;
    }
  }

  @OnChange('unboxed-autocomplete-state')
  public onChangeFieldState(e: ChangeEvent<string>): void {
    this.setParameterFetchCity(e.value);
  }

  private setParameterFetchCity(dsFederationUnit: string | undefined): void {
    catchRenderException(() => {
      this.fetchCity.setParameters({ dsFederationUnit });
    });
  }

  @OnChange('unboxed-autocomplete-city')
  public onChangeUnboxedAutocompleteCity(e: ChangeEvent<string>): void {
    const api = e.target as unknown as componentApi<any>;
    const cdCityIbge = e.value;
    const cities = (api.state.data as unknown as City[]) ?? [];
    this.city = cities.find((city) => city.cdCityIbge === cdCityIbge);
  }

  @OnBlur('field-postalCode')
  public onBlurPostalCode(event: FocusEvent): void {
    const api = event.target as unknown as FieldAPI;
    const postalCode = api.getValue();

    if (api.isValid() && postalCode) {
      void HttpService.get<Address>(Endpoint.GET.ADDRESS_BY_CODE, { postalCode })
        .then((response) => this.setAddress(response));
    }
  }

  @OnChange(Aliases.FIELD_ESTABLISHMENT_TYPE)
  onChangeToBranch(event: ChangeEvent<any>): void {
    const data = event.value;
    this.headquartersIdInput.setEnabled(data.value == 'F');

    if (data.value == IeEstablishment.BRANCH) {
      this.headquartersIdField.setValidators([{ name: 'required', options: {} }] as Validator[]);
      this.headquartersIdField.validate();

    } else {
      this.headquartersIdField.setValue('');
      this.headquartersIdField.setValidators([{}] as Validator[]);
      this.headquartersIdField.validate();
      this.headquartersIdField.setState({ showValidations: false });
    }

  }

  private setAddress(response: RequestResponse<Address>): void {
    if (response.body) {
      const { dsFederationUnit, dsAddress } = response.body;

      this.formEstablishment.setData({
        ...this.formEstablishment.getRecord(),
        ...response.body,
        address: dsAddress,
        sgState: dsFederationUnit,
      });

      if (dsFederationUnit) {
        this.setParameterFetchCity(dsFederationUnit);
      }
    }
  }

  @OnChange('field-state')
  public onChangeSgState(e: ChangeEvent<string>): void {
    this.fieldCity.setValue(undefined);
    this.fetchCity.setParameters({ dsFederationUnit: e.value ? e.value : null });
  }

  @OnMount('form-establishment')
  onMountForm(event: MountEvent): void {
    const api = event.target as FormAPI;
    FormViewService.shouldEditForm(api);
  }

  private setRadioButtonEstablishmentOptions(): void {
    catchRenderException(() => {
      this.radioGroupEstablishmentType.setData([this.ieTypeHead, this.ieTypeBranch]);
    });
  }

}
